import { action, observable, makeObservable } from "mobx";

import { Customer, CustomerRequestBody, CustomerAlertType, CustomerStorageConfig, StorageChargeCurrency } from "../../../models/Customer/Customer";
import CustomerAPI from "../api/CustomerAPI";
import { ActivityLog } from "../../../models/Activity/ActivityLog";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { GetCustomerParams } from "../../../models/Customer/getCustomerParams";

export interface CustomerApiKeyItem {
  id: number;
  customer_id: number;
  api_user_id: number;
  name: string | null;
  key_prefix: string;
  masked_api_key: string;
  masked_secret_key: string;
  is_active: boolean;
  status: "Active" | "Disabled";
  last_used_at: string | null;
  created_at: string;
  created_by: number | null;
}

export const DEFAULT_REDEMPTION_EMAIL = "rewards@redeem.swageazy.com";

export class CustomerStore {
  customers: Customer[];
  customerAPI: CustomerAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  customerDetails: Customer;
  originalCustomer: Customer;
  permissionMap: Map<string, string>;
  activityLogs: ActivityLog[];
  partnerApiKeys: CustomerApiKeyItem[];
  isPartnerApiKeysLoading: boolean;

  constructor() {
    this.customers = [];
    this.customerAPI = new CustomerAPI();
    this.permissionMap = new Map<string, string>();
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.activityLogs = [];
    this.partnerApiKeys = [];
    this.isPartnerApiKeysLoading = false;
    this.customerDetails = {};
    this.originalCustomer = {};
    this.clearCustomerDetails();

    makeObservable(this, {
      customers: observable,
      customerDetails: observable,
      originalCustomer: observable,
      updateName: action,
      updateLegalName: action,
      trimTextFields: action,
      getCustomers: action,
      discardChanges: action,
      searchCustomers: action,
      updateCustomerDetails: action,
      updateRedemptionMail: action,
      clearCustomerDetails: action,
      updateLocalCustomerDetails: action,
      updateEmailFrom: action,
      updateUrl: action,
      updateRedemptionEmail: action,
      updateEnableCoupon: action,
      updateEnableRedemptionEmail: action,
      updateEnableInventory: action,
      updateEnableGOC: action,
      updateEnableCampaign: action,
      updateEnableSwagStore: action,
      updateEnableShopify: action,
      updateEnableAlert: action,
      updateAlertType: action,
      updateRedirectToLinkedIn: action,
      updateStorageCharge: action,
      updateStorageCurrency: action,
      createCustomer: action,
      updateEnableSez: action,
      updateZohoId: action,
      updateHrOrderId: action,
      updateMarketingOrderId: action,
      updateGoldstandardOrderId: action,
      updateIsPartner: action,
      activityLogs: observable,
      getCustomerActivityLogs: action,
      activateCustomer: action,
      clearActivityLogs: action,
      partnerApiKeys: observable,
      isPartnerApiKeysLoading: observable,
      fetchPartnerApiKeys: action,
      disablePartnerApiKey: action,
    });
  }

  searchCustomers = async (filter: GetCustomerParams) => {
    try {
      const response = await this.customerAPI.getCustomers(filter);
      this.customers = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  hasPermission = (role: EmployeeRoles | [EmployeeRoles]): boolean => {
    let hasAccess = false;

    if (this.permissionMap.size === 0) return hasAccess;
    if (this.permissionMap.get(EmployeeRoles.ALL)) return true;

    if (Array.isArray(role)) {
      for (let i in role) {
        const item = role[i];
        const permission = this.permissionMap.get(item);
        if (permission) {
          hasAccess = true;
          break;
        }
      }
    } else {
      const permission = this.permissionMap.get(role);
      if (permission) hasAccess = true;
    }

    return hasAccess;
  };

  clearCustomerDetails = () => {
    const defaultDetails: Partial<Customer> = {
      is_partner: false,
      config: {
        email: DEFAULT_REDEMPTION_EMAIL,
        send_redemption_email: false,
        inventory_enabled: false,
        gift_of_choice_enabled: false,
        campaigns_enabled: false,
        swagstore_enabled: false,
        shopify_enabled: false,
        coupon_redemption_enabled: false,
        is_sez: false,
        alert_enabled: false,
        alert_type: CustomerAlertType.EMAIL,
        redirect_redemptions_to_linkedin: false,
      },
      storage_config: {
        currency: "USD",
        s_labour_charge: "",
        s_package_charge: "",
        s_storage_charge: "",
        m_labour_charge: "",
        m_package_charge: "",
        m_storage_charge: "",
        l_labour_charge: "",
        l_package_charge: "",
        l_storage_charge: "",
        sp_labour_charge: "",
        sp_package_charge: "",
        sp_storage_charge: "",
      },
    };
    this.originalCustomer = JSON.parse(JSON.stringify(defaultDetails)) as Customer;
    this.customerDetails = JSON.parse(JSON.stringify(defaultDetails)) as Customer;
  };

  getCustomers = async (filter: GetCustomerParams) => {
    try {
      const response = await this.customerAPI.getCustomers(filter);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.customers = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  reset = () => {
    this.customers = [];
    this.clearCustomerDetails();
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
  };

  /** Trims leading and trailing spaces on customer text fields. */
  trimTextFields = () => {
    const details = this.customerDetails;
    const trim = (value?: string | null) =>
      typeof value === "string" ? value.trim() : value;

    details.name = trim(details.name) ?? undefined;
    details.legal_name = trim(details.legal_name) ?? undefined;
    details.zoho_id = trim(details.zoho_id) ?? undefined;
    details.hr_order_id = trim(details.hr_order_id) ?? undefined;
    details.marketing_order_id = trim(details.marketing_order_id) ?? undefined;
    details.goldstandard_order_id =
      trim(details.goldstandard_order_id) ?? undefined;

    if (details.config) {
      details.config.email = trim(details.config.email) ?? undefined;
      details.config.from_email_name =
        trim(details.config.from_email_name) ?? undefined;
      details.config.redemption_url =
        trim(details.config.redemption_url) ?? undefined;
    }

    const storage = details.storage_config;
    if (storage) {
      const chargeKeys = [
        "s_labour_charge",
        "s_package_charge",
        "s_storage_charge",
        "m_labour_charge",
        "m_package_charge",
        "m_storage_charge",
        "l_labour_charge",
        "l_package_charge",
        "l_storage_charge",
        "sp_labour_charge",
        "sp_package_charge",
        "sp_storage_charge",
      ] as const;
      chargeKeys.forEach((key) => {
        const value = storage[key];
        if (typeof value === "string") {
          storage[key] = value.trim();
        }
      });
    }
  };

  /**
   * `includeStorageConfig` is false for admins without the ALL permission,
   * so their saves never send (or overwrite) storage charges.
   */
  updateCustomerDetails = async (includeStorageConfig = true) => {
    try {
      if (!this.customerDetails.id) return;
      this.trimTextFields();

      const customerRequest = {
        id: this.customerDetails.id,
        name: this.customerDetails.name,
        legal_name: this.customerDetails.legal_name,
        redemption_url: this.customerDetails.config?.redemption_url,
        redemption_email:
          this.customerDetails.config?.email &&
          this.customerDetails.config.email.trim() !== ""
            ? this.customerDetails.config.email.trim()
            : DEFAULT_REDEMPTION_EMAIL,
        redemption_email_from: this.customerDetails.config?.from_email_name,
        enable_coupon_redemption:
          Boolean(this.customerDetails.config?.send_redemption_email),
        enable_email_redemption:
          Boolean(this.customerDetails.config?.send_redemption_email),
        enable_inventory: this.customerDetails.config?.inventory_enabled,
        enable_goc: this.customerDetails.config?.gift_of_choice_enabled,
        enable_campaigns: this.customerDetails.config?.campaigns_enabled,
        enable_swagstore: this.customerDetails.config?.swagstore_enabled,
        // Shopify UI removed — always persist false for backward-compatible API.
        enable_shopify: false,
        is_sez: this.customerDetails.config?.is_sez,
        inventory_type: this.customerDetails.config?.inventory_type,
        zoho_id: this.customerDetails.zoho_id,
        hr_order_id: this.customerDetails.hr_order_id,
        marketing_order_id: this.customerDetails.marketing_order_id,
        goldstandard_order_id: this.customerDetails.goldstandard_order_id,
        is_partner: this.customerDetails.is_partner,
        enable_alert: this.customerDetails.config?.alert_enabled ?? false,
        alert_type: this.customerDetails.config?.alert_enabled
          ? (this.customerDetails.config?.alert_type || CustomerAlertType.EMAIL)
          : undefined,
        redirect_redemptions_to_linkedin:
          Boolean(this.customerDetails.config?.send_redemption_email)
            ? Boolean(this.customerDetails.config?.redirect_redemptions_to_linkedin)
            : false,
        storage_config: includeStorageConfig && this.customerDetails.storage_config
          ? {
              currency: this.customerDetails.storage_config.currency || "USD",
              s_labour_charge: this.customerDetails.storage_config.s_labour_charge ?? "",
              s_package_charge: this.customerDetails.storage_config.s_package_charge ?? "",
              s_storage_charge: this.customerDetails.storage_config.s_storage_charge ?? "",
              m_labour_charge: this.customerDetails.storage_config.m_labour_charge ?? "",
              m_package_charge: this.customerDetails.storage_config.m_package_charge ?? "",
              m_storage_charge: this.customerDetails.storage_config.m_storage_charge ?? "",
              l_labour_charge: this.customerDetails.storage_config.l_labour_charge ?? "",
              l_package_charge: this.customerDetails.storage_config.l_package_charge ?? "",
              l_storage_charge: this.customerDetails.storage_config.l_storage_charge ?? "",
              sp_labour_charge: this.customerDetails.storage_config.sp_labour_charge ?? "",
              sp_package_charge: this.customerDetails.storage_config.sp_package_charge ?? "",
              sp_storage_charge: this.customerDetails.storage_config.sp_storage_charge ?? "",
            }
          : undefined,
      };

      const response =
        await this.customerAPI.updateCustomerDetails(customerRequest);

      this.updateLocalCustomerDetails(response.result);
      if (response?.message && response.result) {
        (response.result as any).api_message = response.message;
      }
      return response.result;
    } catch (error: any) {
      throw error;
    }
  };

  updateName = (name: string) => {
    this.customerDetails.name = name;
  };

  updateLegalName = (legalName: string) => {
    this.customerDetails.legal_name = legalName;
  };

  updateRedemptionMail = (redemptionMail: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      redemption_email: redemptionMail,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEmailFrom = (emailFrom: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      from_email_name: emailFrom,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateZohoId = (zohoId: string) => {
    this.customerDetails.zoho_id = zohoId;
  };

  updateHrOrderId = (hrOrderId: string) => {
    this.customerDetails.hr_order_id = hrOrderId;
  };

  updateMarketingOrderId = (marketingOrderId: string) => {
    this.customerDetails.marketing_order_id = marketingOrderId;
  };

  updateGoldstandardOrderId = (goldstandardOrderId: string) => {
    this.customerDetails.goldstandard_order_id = goldstandardOrderId;
  };

  updateIsPartner = (isPartner: boolean) => {
    this.customerDetails.is_partner = isPartner;
  };

  updateRedemptionEmail = (redemptionMail: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      email: redemptionMail,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateUrl = (redemptionUrl: string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      redemption_url: redemptionUrl,
    };
    this.customerDetails.config = updatedConfig;
  };

  updateEnableCoupon = (enableCoupon: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      coupon_redemption_enabled: enableCoupon,
      send_redemption_email: enableCoupon,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateInventoryType = (inventoryType: any) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      inventory_type: inventoryType,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableRedemptionEmail = (redemptionMail: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      send_redemption_email: redemptionMail,
      coupon_redemption_enabled: redemptionMail,
      redirect_redemptions_to_linkedin: redemptionMail
        ? (config.redirect_redemptions_to_linkedin ?? false)
        : false,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableInventory = (inventory: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      inventory_enabled: inventory,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableGOC = (goc: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      gift_of_choice_enabled: goc,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableCampaign = (campaign: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      campaigns_enabled: campaign,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableSwagStore = (swagstore: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      swagstore_enabled: swagstore,
    };

    this.customerDetails.config = updatedConfig;
  };

  /**
   * Shopify is no longer configurable in the UI; keep the flag forced off locally.
   */
  updateEnableShopify = (_shopify?: boolean) => {
    const config = this.customerDetails.config || {};
    this.customerDetails.config = {
      ...config,
      shopify_enabled: false,
    };
  };

  updateEnableSez = (sez: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      is_sez: sez,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateEnableAlert = (alertEnabled: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      alert_enabled: alertEnabled,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateAlertType = (alertType: CustomerAlertType | string) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      alert_type: alertType,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateRedirectToLinkedIn = (redirectToLinkedIn: boolean) => {
    const config = this.customerDetails.config || {};

    const updatedConfig = {
      ...config,
      redirect_redemptions_to_linkedin: redirectToLinkedIn,
    };

    this.customerDetails.config = updatedConfig;
  };

  updateLocalCustomerDetails = (customer: Customer) => {
    if (customer) {
      if (!customer.config) {
        customer.config = { email: DEFAULT_REDEMPTION_EMAIL };
      } else if (!customer.config.email || customer.config.email.trim() === "") {
        customer.config.email = DEFAULT_REDEMPTION_EMAIL;
      }
      if (!customer.storage_config) {
        customer.storage_config = {
          currency: "USD",
          s_labour_charge: "",
          s_package_charge: "",
          s_storage_charge: "",
          m_labour_charge: "",
          m_package_charge: "",
          m_storage_charge: "",
          l_labour_charge: "",
          l_package_charge: "",
          l_storage_charge: "",
          sp_labour_charge: "",
          sp_package_charge: "",
          sp_storage_charge: "",
        };
      }
    }
    this.customerDetails = customer;
    this.originalCustomer = JSON.parse(JSON.stringify(customer));
  };

  getCustomerDetailsById = async (customerId: number | null) => {
    try {
      const response = await this.customerAPI.getCustomerDetailsById(
        customerId!,
      );
      this.updateLocalCustomerDetails(response);
    } catch (error: any) {
      throw error;
    }
  };

  discardChanges = () => {
    this.customerDetails = JSON.parse(JSON.stringify(this.originalCustomer));
  };

  updateStorageCharge = (
    size: "s" | "m" | "l" | "sp",
    field: "labour_charge" | "package_charge" | "storage_charge",
    value: string | number,
  ) => {
    if (!this.customerDetails.storage_config) {
      this.customerDetails.storage_config = {
        currency: "USD",
      };
    }
    const key = `${size}_${field}` as keyof CustomerStorageConfig;
    (this.customerDetails.storage_config as any)[key] = value;
  };

  updateStorageCurrency = (currency: StorageChargeCurrency | "INR" | "USD") => {
    if (!this.customerDetails.storage_config) {
      this.customerDetails.storage_config = {
        currency,
      };
    } else {
      this.customerDetails.storage_config.currency = currency;
    }
  };

  createCustomer = async (
    customerDetails: CustomerRequestBody,
    includeStorageConfig = true,
  ) => {
    try {
      this.trimTextFields();
      const customerRequest: CustomerRequestBody = {
        id: this.customerDetails.id,
        name: this.customerDetails.name ?? "",
        legal_name: this.customerDetails.legal_name ?? "",
        redemption_url: this.customerDetails.config?.redemption_url ?? "",
        redemption_email:
          this.customerDetails.config?.email &&
          this.customerDetails.config.email.trim() !== ""
            ? this.customerDetails.config.email.trim()
            : DEFAULT_REDEMPTION_EMAIL,
        redemption_email_from:
          this.customerDetails.config?.from_email_name ?? "",
        enable_coupon_redemption:
          Boolean(this.customerDetails.config?.send_redemption_email),
        enable_email_redemption:
          Boolean(this.customerDetails.config?.send_redemption_email),
        enable_inventory:
          this.customerDetails.config?.inventory_enabled ?? false,
        enable_goc:
          this.customerDetails.config?.gift_of_choice_enabled ?? false,
        enable_campaigns:
          this.customerDetails.config?.campaigns_enabled ?? false,
        enable_swagstore:
          this.customerDetails.config?.swagstore_enabled ?? false,
        // Shopify UI removed — always persist false for backward-compatible API.
        enable_shopify: false,
        is_sez: this.customerDetails.config?.is_sez ?? false,
        inventory_type: this.customerDetails.config?.inventory_type,
        hr_order_id: this.customerDetails.hr_order_id ?? "",
        marketing_order_id: this.customerDetails.marketing_order_id ?? "",
        goldstandard_order_id: this.customerDetails.goldstandard_order_id ?? "",
        is_partner: this.customerDetails.is_partner ?? false,
        zoho_id: this.customerDetails.zoho_id,
        enable_alert: this.customerDetails.config?.alert_enabled ?? false,
        alert_type: this.customerDetails.config?.alert_enabled
          ? (this.customerDetails.config?.alert_type || CustomerAlertType.EMAIL)
          : undefined,
        redirect_redemptions_to_linkedin:
          Boolean(this.customerDetails.config?.send_redemption_email)
            ? Boolean(this.customerDetails.config?.redirect_redemptions_to_linkedin)
            : false,
        storage_config: includeStorageConfig && this.customerDetails.storage_config
          ? {
              currency: this.customerDetails.storage_config.currency || "USD",
              s_labour_charge: this.customerDetails.storage_config.s_labour_charge ?? "",
              s_package_charge: this.customerDetails.storage_config.s_package_charge ?? "",
              s_storage_charge: this.customerDetails.storage_config.s_storage_charge ?? "",
              m_labour_charge: this.customerDetails.storage_config.m_labour_charge ?? "",
              m_package_charge: this.customerDetails.storage_config.m_package_charge ?? "",
              m_storage_charge: this.customerDetails.storage_config.m_storage_charge ?? "",
              l_labour_charge: this.customerDetails.storage_config.l_labour_charge ?? "",
              l_package_charge: this.customerDetails.storage_config.l_package_charge ?? "",
              l_storage_charge: this.customerDetails.storage_config.l_storage_charge ?? "",
              sp_labour_charge: this.customerDetails.storage_config.sp_labour_charge ?? "",
              sp_package_charge: this.customerDetails.storage_config.sp_package_charge ?? "",
              sp_storage_charge: this.customerDetails.storage_config.sp_storage_charge ?? "",
            }
          : undefined,
      };

      const response = await this.customerAPI.createCustomer(customerRequest);

      this.customerDetails = response.result || [];
      return response;
    } catch (error: any) {
      console.error("Error creating customer:", error);
      throw error;
    }
  };

  deleteCustomer = async (customerId: number) => {
    try {
      const response = await this.customerAPI.deleteCustomer(customerId);
      if (response.success && this.customerDetails) {
        const index = this.customers.findIndex(
          (item) => item.id === customerId,
        );
        if (index !== -1) {
          this.customers[index] = {
            ...this.customers[index],
          };
        }
      }
      return response;
    } catch (error: any) {
      console.error("Error creating customer:", error);
      throw error;
    }
  };

  activateCustomer = async (customerId: number) => {
    try {
      const response = await this.customerAPI.activateCustomer(customerId);
      if (response.success) {
        const index = this.customers.findIndex(
          (item) => item.id === customerId,
        );
        if (index !== -1) {
          this.customers[index] = {
            ...this.customers[index],
            is_deleted: 0,
          };
        }
      }
      return response;
    } catch (error: any) {
      console.error("Error activating customer:", error);
      throw error;
    }
  };

  getCustomerActivityLogs = async (customerId: number) => {
    try {
      const response =
        await this.customerAPI.getCustomerActivityLogs(customerId);
      this.activityLogs = response;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  clearActivityLogs = () => {
    this.activityLogs = [];
  };

  generatePartnerApiKey = async (customerId: number, name?: string) => {
    try {
      const response = await this.customerAPI.generatePartnerApiKey(
        customerId,
        name
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  fetchPartnerApiKeys = async (customerId: number) => {
    this.isPartnerApiKeysLoading = true;
    try {
      const response = await this.customerAPI.getPartnerApiKeys(customerId);
      this.partnerApiKeys = response?.result || [];
      return this.partnerApiKeys;
    } catch (error: any) {
      this.partnerApiKeys = [];
      throw error;
    } finally {
      this.isPartnerApiKeysLoading = false;
    }
  };

  disablePartnerApiKey = async (customerId: number, keyId: number) => {
    try {
      const response = await this.customerAPI.disablePartnerApiKey(customerId, keyId);
      if (response?.result) {
        this.partnerApiKeys = this.partnerApiKeys.map((key) =>
          key.id === keyId ? response.result : key
        );
      } else {
        await this.fetchPartnerApiKeys(customerId);
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };
}
